import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'

/**
 * A site's bill of quantities.
 *
 * <p>Everything recorded daily hangs off these lines — work completion is booked against
 * one, progress percentages divide by its planned quantity, and the programme measures
 * activities through the lines they cover. A site without a BOQ cannot be worked on, so
 * this is the first thing set up after the site itself.</p>
 */

export type WorkItemStatus = 'PLANNED' | 'PROCESSING' | 'COMPLETED' | 'PENDING'

export interface WorkItem {
  id: string
  siteId: string
  code: string
  description: string
  workPackageId: string
  workPackageName?: string
  workCategoryId?: string
  siteLocationId?: string
  uomId: string
  uomCode?: string
  totalPlannedQuantity: number
  completedQuantity: number
  balanceQuantity: number
  percentComplete?: number
  weightage: number
  plannedStartDate?: string
  plannedFinishDate?: string
  actualStartDate?: string
  expectedFinishDate?: string
  status: WorkItemStatus
  delayed: boolean
  /** True once a daily report has booked progress, which blocks deletion. */
  hasProgress: boolean
  remarks?: string
  version: number
}

export interface WorkItemRequest {
  code: string
  description: string
  workPackageId: string
  uomId: string
  totalPlannedQuantity: number
  weightage?: number | null
  plannedStartDate?: string | null
  plannedFinishDate?: string | null
  status?: WorkItemStatus
  remarks?: string
}

export function useWorkItems(siteId?: string) {
  return useQuery({
    enabled: Boolean(siteId),
    queryKey: ['work-items', siteId],
    queryFn: async () => {
      const { data } = await api.get<WorkItem[]>(`/sites/${siteId}/work-items`)
      return data
    },
  })
}

function useWorkItemMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-items'] })
      // The entry screens read the BOQ through their own prefill call.
      void qc.invalidateQueries({ queryKey: ['work-prefill'] })
      void qc.invalidateQueries({ queryKey: ['network'] })
    },
  })
}

export function useCreateWorkItem(siteId: string) {
  return useWorkItemMutation(async (body: WorkItemRequest) => {
    const { data } = await api.post<WorkItem>(`/sites/${siteId}/work-items`, body)
    return data
  })
}

/** A bill arrives as a list, so it can be added as one. */
export function useCreateWorkItems(siteId: string) {
  return useWorkItemMutation(async (items: WorkItemRequest[]) => {
    const { data } = await api.post<WorkItem[]>(`/sites/${siteId}/work-items/bulk`, { items })
    return data
  })
}

export function useUpdateWorkItem(siteId: string) {
  return useWorkItemMutation(async ({ id, body }: { id: string; body: WorkItemRequest }) => {
    const { data } = await api.put<WorkItem>(`/sites/${siteId}/work-items/${id}`, body)
    return data
  })
}

export function useDeleteWorkItem(siteId: string) {
  return useWorkItemMutation(async (id: string) => {
    await api.delete(`/sites/${siteId}/work-items/${id}`)
  })
}
